import React from 'react';
import OtpInput from 'react-otp-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FormItem, Subtitle } from '../styles';
import styled from 'styled-components';

const ResendLink = styled.div`
  text-align: right;
  margin-top: 4px;
  padding-right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
  max-width: 436px;

  button {
    background: none;
    border: none;
    color: #2D6CCA;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 8px;
    transition: all 0.2s ease;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover:not(:disabled) {
      color: #2563c7;
      text-decoration: underline;
    }

    @media (max-width: 480px) {
      font-size: 13px;
      padding: 3px 6px;
      gap: 5px;
    }

    @media (max-width: 360px) {
      font-size: 12px;
      padding: 2px 4px;
      gap: 4px;
    }
  }

  @media (max-width: 768px) {
    max-width: 390px;
  }

  @media (max-width: 480px) {
    margin-top: 3px;
    gap: 6px;
    max-width: 356px;
  }

  @media (max-width: 360px) {
    margin-top: 2px;
    gap: 4px;
    max-width: 322px;
  }
`;

const ResendLoadingIcon = styled(FontAwesomeIcon)`
  animation: spin 1s linear infinite;
  font-size: 12px;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }

  @media (max-width: 360px) {
    font-size: 10px;
  }
`;

const OtpContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px 0 0 0;
  padding: 0;
`;

const OtpInputWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 100%;

  .otp-container {
    display: flex !important;
    gap: 20px !important;
    justify-content: center !important;
    align-items: center !important;

    @media (max-width: 768px) {
      gap: 18px !important;
    }

    @media (max-width: 480px) {
      gap: 16px !important;
    }

    @media (max-width: 360px) {
      gap: 14px !important;
    }
  }

  input {
    width: 56px !important;
    height: 56px !important;
    border-radius: 12px !important;
    border: 1px solid #e0e0e0 !important;
    background-color: #ffffff !important;
    font-size: 20px !important;
    font-weight: 700 !important;
    text-align: center !important;
    color: #1a202c !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04) !important;
    margin-right: 20px !important;
    margin-left: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;

    &:last-child {
      margin-right: 0 !important;
    }

    &::placeholder {
      color: #cbd5e0 !important;
    }

    &:hover:not(:disabled) {
      border-color: #2D6CCA !important;
      box-shadow: 0 4px 8px rgba(45, 108, 202, 0.12) !important;
    }

    &:focus {
      border-color: #2D6CCA !important;
      outline: none !important;
      box-shadow: 0 0 0 4px rgba(45, 108, 202, 0.15), 0 4px 12px rgba(45, 108, 202, 0.2) !important;
    }

    &:disabled {
      background-color: #f7fafc !important;
      cursor: not-allowed !important;
      opacity: 0.6 !important;
    }

    @media (max-width: 768px) {
      width: 50px !important;
      height: 50px !important;
      font-size: 18px !important;
      border-radius: 10px !important;
      margin-right: 18px !important;
      margin-left: 0 !important;

      &:last-child {
        margin-right: 0 !important;
      }
    }

    @media (max-width: 480px) {
      width: 46px !important;
      height: 46px !important;
      font-size: 17px !important;
      border-radius: 10px !important;
      margin-right: 16px !important;
      margin-left: 0 !important;

      &:last-child {
        margin-right: 0 !important;
      }
    }

    @media (max-width: 360px) {
      width: 42px !important;
      height: 42px !important;
      font-size: 16px !important;
      border-radius: 8px !important;
      margin-right: 14px !important;
      margin-left: 0 !important;

      &:last-child {
        margin-right: 0 !important;
      }
    }
  }
`;

interface OTPInputFieldsProps {
  otp: string[];
  onOtpChange: (index: number, value: string) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onResendOtp?: () => void;
  isResendDisabled?: boolean;
  resendTimer?: number;
  isResending?: boolean;
}

const OTPInputFields: React.FC<OTPInputFieldsProps> = ({ 
  otp, 
  onOtpChange, 
  onKeyDown,
  onResendOtp,
  isResendDisabled,
  resendTimer,
  isResending
}) => {
  const otpString = otp.join('');

  const handleChange = (otpValue: string): void => {
    if (otpValue.length <= 6 && /^\d*$/.test(otpValue)) {
      const newOtp = otpValue.split('');
      for (let i = 0; i < 6; i++) {
        const value = newOtp[i] || '';
        if (otp[i] !== value) {
          onOtpChange(i, value);
        }
      }
      
      if (otpValue.length < 6) {
        for (let i = otpValue.length; i < 6; i++) {
          if (otp[i] !== '') {
            onOtpChange(i, '');
          }
        }
      }
    }
  };

  return (
    <FormItem>
      <Subtitle style={{ marginBottom: '12px', fontSize: '15px', fontWeight: 500 }}>
        Enter the 6-digit code
      </Subtitle>
      <OtpContainerWrapper>
        <OtpInputWrapper>
          <OtpInput
            value={otpString}
            onChange={handleChange}
            numInputs={6}
            renderInput={(inputProps, index) => (
              <input
                {...inputProps}
                data-index={index}
                id={`otp-input-${index}`}
                onKeyDown={(e) => onKeyDown(index, e)}
                placeholder="-"
              />
            )}
            inputType="number"
            shouldAutoFocus
          />
        </OtpInputWrapper>
        
        {onResendOtp && (
          <ResendLink>
            <button
              type="button"
              onClick={onResendOtp}
              disabled={isResendDisabled || isResending}
            >
              {isResending && <ResendLoadingIcon icon={faSpinner} />}
              {isResendDisabled 
                ? `Resend OTP in ${Math.ceil(resendTimer || 0)}s` 
                : 'Resend OTP'
              }
            </button>
          </ResendLink>
        )}
      </OtpContainerWrapper>
    </FormItem>
  );
};

export default OTPInputFields;
